// Notification Service - Event-based notification system for the application

export type NotificationType = 'success' | 'info' | 'warning' | 'error';

export interface NotificationPayload {
  type: NotificationType;
  title: string;
  message: string;
  autoClose?: boolean; // Auto close after duration (default: true for success/info)
  duration?: number; // Duration in ms (default: 5000)
}

export interface Notification extends NotificationPayload {
  id: number;
  time: string;
  read: boolean;
}

// Event name for notification events
const NOTIFICATION_EVENT = 'app:notification';

// Generate unique ID
let notificationIdCounter = Date.now();
const generateId = (): number => ++notificationIdCounter;

// Format time as relative string
const formatTime = (): string => 'Just now';

// Create and dispatch a notification event
const dispatchNotification = (payload: NotificationPayload): Notification => {
  const notification: Notification = {
    ...payload,
    id: generateId(),
    time: formatTime(),
    read: false,
  };

  const event = new CustomEvent<Notification>(NOTIFICATION_EVENT, {
    detail: notification,
  });

  window.dispatchEvent(event);
  return notification;
};

// Notification service object with methods for each type
export const notificationService = {
  // Show success notification
  success: (title: string, message: string, options?: Partial<NotificationPayload>): Notification => {
    return dispatchNotification({
      type: 'success',
      title,
      message,
      autoClose: true,
      duration: 5000,
      ...options,
    });
  },

  // Show info notification
  info: (title: string, message: string, options?: Partial<NotificationPayload>): Notification => {
    return dispatchNotification({
      type: 'info',
      title,
      message,
      autoClose: true,
      duration: 5000,
      ...options,
    });
  },

  // Show warning notification
  warning: (title: string, message: string, options?: Partial<NotificationPayload>): Notification => {
    return dispatchNotification({
      type: 'warning',
      title,
      message,
      autoClose: false,
      ...options,
    });
  },

  // Show error notification
  error: (title: string, message: string, options?: Partial<NotificationPayload>): Notification => {
    return dispatchNotification({
      type: 'error',
      title,
      message,
      autoClose: false,
      ...options,
    });
  },

  // Generic notify method
  notify: (payload: NotificationPayload): Notification => {
    return dispatchNotification(payload);
  },

  // Subscribe to notification events (used by main_layout)
  subscribe: (callback: (notification: Notification) => void): (() => void) => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<Notification>;
      callback(customEvent.detail);
    };

    window.addEventListener(NOTIFICATION_EVENT, handler);

    // Return unsubscribe function
    return () => {
      window.removeEventListener(NOTIFICATION_EVENT, handler);
    };
  },
};

// Default export
export default notificationService;
